import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  formatarMilhar,
  formatarPorcentagem,
} from '../../../../helpers/formatador';
import apiAnalytics from '../../../../services/apiAnalytics';
import { Container, Spin, Styles } from './styles';

const HistoricoDeCarregamento = ({
  dataInicial,
  dataFinal,
  tipo,
  cnpj,
  loteId,
  width,
  height,
}) => {
  const [dataDash, setDataDash] = useState([]);
  const [loading, setLoading] = useState(false);

  const legendas = {
    aderencia: 'Aderência',
    cadencia_disponivel: 'Cadência disponível',
    cadencia_preenchida: 'Cadência preenchida',
  };

  const formatterTooltip = (value, name) => {
    const legenda = legendas[name];

    return name === 'aderencia'
      ? [formatarPorcentagem(value), legenda]
      : [formatarMilhar(value, 0, false), legenda];
  };

  const formatterLegendText = (value, entry) => {
    const { color } = entry;

    return <span style={{ color }}>{legendas[value]}</span>;
  };

  const transformaData = aderencia => {
    const label = [];
    aderencia.forEach(arr => {
      const dados = arr.data.reduce((obj, val) => {
        obj.aderencia = parseInt(val.value, 10);
        obj.cadencia_disponivel = parseInt(val.cadenciaDisponivel, 10);
        obj.cadencia_preenchida = parseInt(val.cadenciaPreenchida, 10);
        return obj;
      }, {});
      label.push({
        name: arr.label,
        ...dados,
      });
    });

    return label;
  };

  const createLabelDaily = (dtInicial, dtFinal) => {
    const labels = [];
    const inicio = moment(dtInicial).startOf('days');
    const fim = moment(dtFinal).endOf('days');
    for (
      let dtIni = inicio;
      dtIni.isBefore(fim, 'minute');
      dtIni.add(1, 'days')
    ) {
      labels.push(dtIni.format('D/M'));
    }

    return labels;
  };

  const getAderencia = useCallback(
    async (dIncial, dFinal) => {
      try {
        setLoading(true);

        const size = moment(dFinal).diff(dIncial, 'days');

        const query = {
          type: 'DAILY',
          dataInicial: dIncial.format(),
          dataFinal: dFinal.format(),
          cnpjs: [cnpj],
          lotesId: [loteId],
          size,
        };

        const aderencias = await apiAnalytics.get(
          '/relatorios/aderencia-carregamento',
          {
            params: { ...query },
          }
        );

        const labels = createLabelDaily(dIncial, dFinal);

        const dados = labels.map(label => ({
          data: [],
          label,
        }));

        dados.forEach(dado => {
          aderencias.data.forEach(variacao => {
            if (dado.label === variacao.label) {
              dado.data = [...dado.data, ...variacao.data];
            }
          });
        });

        const dataDashResponse = transformaData(dados);

        setDataDash(dataDashResponse);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [tipo]
  );

  useEffect(() => {
    getAderencia(dataInicial, dataFinal);
  }, [getAderencia, dataInicial, dataFinal]);

  return (
    <>
      <Styles />
      <Container
        type="flex"
        align="middle"
        justify="center"
        className={loading ? 'chart-custom-loading' : 'chart-custom'}
      >
        <Spin size="large" spinning={loading} />
        <ResponsiveContainer width={width} height={height}>
          <ComposedChart
            data={dataDash}
            margin={{
              top: 18,
              right: 0,
              left: 0,
              bottom: 10,
            }}
            barGap={12}
            barCategoryGap={120}
          >
            <CartesianGrid horizontal vertical={false} />
            <XAxis dataKey="name" />
            <YAxis
              yAxisId="left"
              orientation="left"
              type="number"
              tickFormatter={toneladas => formatarMilhar(toneladas, 0, false)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              type="number"
              tickFormatter={aderencia =>
                formatarPorcentagem(aderencia, '0 %', 0)
              }
              domain={[0, 100]}
            />

            <Tooltip formatter={formatterTooltip} />
            <Legend formatter={formatterLegendText} />
            <Bar
              yAxisId="left"
              key="cadencia_disponivel"
              dataKey="cadencia_disponivel"
              fill="#24A95D"
              barSize={dataDash.length > 8 ? 22 : 32}
              radius={4}
            />
            <Bar
              yAxisId="left"
              key="cadencia_preenchida"
              dataKey="cadencia_preenchida"
              fill="#4190F7"
              barSize={dataDash.length > 8 ? 22 : 32}
              radius={4}
            />
            <Line
              yAxisId="right"
              key="aderencia"
              dataKey="aderencia"
              stroke="#E47515"
              type="linear"
              strokeWidth={2}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Container>
    </>
  );
};

HistoricoDeCarregamento.propTypes = {
  tipo: PropTypes.string.isRequired,
  dataInicial: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.any,
  ]).isRequired,
  dataFinal: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.any,
  ]).isRequired,
  loteId: PropTypes.number.isRequired,
  cnpj: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

HistoricoDeCarregamento.defaultProps = {
  width: '100%',
  height: '100%',
};

export default HistoricoDeCarregamento;
